import { z } from 'zod'
import type { ClientKind } from '@/kinds/kind'
import { invalid } from '@/kinds/kind'

/** Well-known ConnectorConfig ids per provider (same as the server; f3 and f4 were Plane's and sdlc's, never reused). */
export const CONNECTOR_IDS: Record<string, string> = {
  forgejo: '00000000-0000-7000-8000-0000000000f1',
  github: '00000000-0000-7000-8000-0000000000f2',
  factory: '00000000-0000-7000-8000-0000000000f5',
}

export const connectorSpec = z
  .object({
    provider: z.enum(['forgejo', 'github', 'factory']),
    enabled: z.boolean().default(false),
    base_url: z.string().max(200).optional(),
    repos: z.array(z.string().regex(/^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/, 'owner/repo')).default([]),
    workspace: z
      .string()
      .regex(/^[a-z0-9][a-z0-9-]{0,30}$/)
      .optional(),
    review_tasks: z.boolean().default(true),
  })
  .strict()
export type ConnectorSpec = z.infer<typeof connectorSpec>

export const connectorStatus = z.object({
  last_poll_at: z.string().optional(),
  last_error: z.string().optional(),
  me: z.string().optional(),
  mirror_count: z.number().int().default(0),
  webhook_seen_at: z.string().optional(),
})
export type ConnectorStatus = z.infer<typeof connectorStatus>

const Nothing = () => null
const RETIRED_KEYS = ['plane_workspace', 'projects']

export const connectorKind: ClientKind<ConnectorSpec, ConnectorStatus> = {
  name: 'ConnectorConfig',
  version: 1,
  spec: connectorSpec,
  status: connectorStatus,
  // plane_workspace and projects were Plane's: dropped, so a row that still carries them stays valid
  defaults: (spec) => {
    const rest: Record<string, unknown> = { ...spec }
    for (const key of RETIRED_KEYS) delete rest[key]
    return { provider: 'forgejo', enabled: false, repos: [], review_tasks: true, ...rest } as ConnectorSpec
  },
  validate: (r) =>
    CONNECTOR_IDS[r.spec.provider] === r.id
      ? []
      : ['ConnectorConfig must use the well-known id for its provider'],
  initialStatus: () => ({ mirror_count: 0 }),
  displayName: (r) => r.spec.provider,
  apply: (_r, action) => {
    throw invalid(`ConnectorConfig has no action ${action}`)
  },
  actions: [],
  ListRow: Nothing,
  Detail: Nothing,
  Form: Nothing,
}
